import { useMemo } from "react";

type FilterCriteria<T> = {
    key: keyof T;
    searchValues: Array<string>;
    objectId?: string; // id on nested objects
    allowNull?: boolean;
    valueToLowercase?: boolean;
    includeEmptyArrays?: boolean; // New flag
};

export function useFilterList<T>(sourceList: Array<T>, filterCriteria: Array<FilterCriteria<T>>, customFilter?: (items: Array<T>) => Array<T>): Array<T> {
    const filteredList = filterCriteria.reduce(
        (filteredData, criteria) => {
            const { key, searchValues, objectId, allowNull, valueToLowercase, includeEmptyArrays } = criteria;

            // If searchValues is empty and returnOriginalOnEmpty is not set or false, continue with filtering
            if (searchValues.length === 0 || searchValues[0] === "") {
                return filteredData;
            }

            return filteredData.filter((entry) => {
                const property = entry[key];

                // Handle Arrays with nested keys
                if (Array.isArray(property) && objectId) {
                    if (includeEmptyArrays && property.length === 0) {
                        return true;
                    }

                    return property.some((item) => isMatching(item[objectId], searchValues, allowNull, valueToLowercase));
                }

                // Handle nested keys on objects
                if (objectId && isObject(property)) {
                    return isMatching(property[objectId], searchValues, allowNull, valueToLowercase);
                }

                // Handle direct key matching
                return isMatching(property, searchValues, allowNull, valueToLowercase);
            });
        },
        [...sourceList]
    );

    return customFilter ? customFilter(filteredList) : filteredList;
}

//function to check if a value matches the criteria
function isMatching(value: any, values: Array<string>, allowNull?: boolean, valueToLowercase?: boolean): boolean {
    if (value === null && allowNull) {
        return values.includes("null");
    }

    const normalizedValue = String(value ?? ""); // Convert to string, handle undefined or null
    const valueToCompare = valueToLowercase ? normalizedValue.toLowerCase() : normalizedValue;

    return values.includes(valueToCompare);
}

//function to check if a value is an object
function isObject(value: any): value is Record<string, any> {
    return value !== null && typeof value === "object";
}
